import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button } from '@epoch-ui/components/Button/button';
import { Question } from 'types/question.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { QuestionFormValues } from '@event-editor/components/EditQuestionSideSheet/config/questionConfig';
import { AddRequiredQuestionConfirmationDialog } from '@event-editor/components/EditQuestionSideSheet/AddRequiredQuestionConfirmationDialog';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { useAddQuestionMutation } from '../mutations/useAddQuestionMutation';
import { useUpdateQuestionMutation } from '../mutations/useUpdateQuestionMutation';
import { UpdateRequiredQuestionConfirmationDialog } from '../UpdateRequiredQuestionConfirmationDialog';

interface SubmitQuestionButtonProps {
  eventId: number;
  existingQuestion?: Question;
  onClose: () => void;
  disabled?: boolean;
}

export const SubmitQuestionButton: React.FC<SubmitQuestionButtonProps> = ({
  eventId,
  existingQuestion,
  onClose,
  disabled
}) => {
  const { guestListTotals, preEventQuestions } = useEditableEvent();
  const confirmedGuests = guestListTotals?.confirmed ?? 0;
  const questionsLength = preEventQuestions?.filter((q) => !q.isDeleted).length || 0;

  const { handleSubmit, formState } = useFormContext<QuestionFormValues>();
  const { showToast } = useToaster();
  const [isAddRequiredQuestionConfirmationOpen, setIsAddRequiredQuestionConfirmationOpen] =
    useState(false);
  const [isUpdateRequiredQuestionConfirmationOpen, setIsUpdateRequiredQuestionConfirmationOpen] =
    useState(false);
  const [pendingQuestionData, setPendingQuestionData] = useState<Question | null>(null);

  const updateQuestionMutation = useUpdateQuestionMutation(
    () => showToast('Failed to update question', { variant: ToasterVariant.ERROR }),
    onClose
  );

  const addQuestionMutation = useAddQuestionMutation(() =>
    showToast('Failed to add question', { variant: ToasterVariant.ERROR })
  );

  const executeAddQuestion = async (questionData: Question): Promise<void> => {
    await addQuestionMutation.mutateAsync({
      eventId,
      question: questionData,
      questionsLength
    });
    onClose();
  };

  const executeUpdateQuestion = async (questionData: Question): Promise<void> => {
    await updateQuestionMutation.mutateAsync({
      eventId,
      question: questionData
    });
  };

  const handleClick = async (formData: QuestionFormValues): Promise<void> => {
    const questionData = { ...existingQuestion, ...formData } as Question;

    if (existingQuestion) {
      // Update existing question - check if switching from non-required to required
      const previousRequired = existingQuestion.isRequired;
      const currentRequired = questionData.isRequired ?? false;
      const shouldShowUpdateConfirmation =
        confirmedGuests > 0 && !previousRequired && currentRequired;

      if (shouldShowUpdateConfirmation) {
        setPendingQuestionData(questionData);
        setIsUpdateRequiredQuestionConfirmationOpen(true);
      } else {
        await executeUpdateQuestion(questionData);
      }
    } else {
      // Add new question - check if confirmation is needed
      const shouldShowAddConfirmation = confirmedGuests > 0 && (questionData.isRequired ?? false);
      if (shouldShowAddConfirmation) {
        setPendingQuestionData(questionData);
        setIsAddRequiredQuestionConfirmationOpen(true);
      } else {
        await executeAddQuestion(questionData);
      }
    }
  };

  const handleConfirmAddRequiredQuestion = async (): Promise<void> => {
    setIsAddRequiredQuestionConfirmationOpen(false);
    if (pendingQuestionData) {
      await executeAddQuestion(pendingQuestionData);
      setPendingQuestionData(null);
    }
  };

  const handleConfirmUpdateRequiredQuestion = async (): Promise<void> => {
    setIsUpdateRequiredQuestionConfirmationOpen(false);
    if (pendingQuestionData) {
      await executeUpdateQuestion(pendingQuestionData);
      setPendingQuestionData(null);
    }
  };

  return (
    <>
      <Button
        data-testid="submit-button"
        variant="primary"
        size="sm"
        className="tw-rounded-xl"
        onClick={handleSubmit(handleClick, (errors) => {
          console.error(errors);
        })}
        disabled={
          !formState.isValid ||
          formState.isSubmitting ||
          addQuestionMutation.isPending ||
          updateQuestionMutation.isPending ||
          disabled
        }
      >
        {existingQuestion ? 'Update' : 'Done'}
      </Button>

      {/* Add required question confirmation dialog */}
      <AddRequiredQuestionConfirmationDialog
        open={isAddRequiredQuestionConfirmationOpen}
        onOpenChange={setIsAddRequiredQuestionConfirmationOpen}
        onConfirm={handleConfirmAddRequiredQuestion}
      />

      {/* Update to required question confirmation dialog */}
      <UpdateRequiredQuestionConfirmationDialog
        open={isUpdateRequiredQuestionConfirmationOpen}
        onOpenChange={setIsUpdateRequiredQuestionConfirmationOpen}
        onConfirm={handleConfirmUpdateRequiredQuestion}
      />
    </>
  );
};
